import React, { Suspense } from 'react';

import { Box } from 'src/design/atoms/Box';
import { Center } from 'src/design/atoms/Center';
import { Loader } from 'src/design/atoms/Loader';
import { Route, Routes, useParams } from 'react-router-dom';
import { useJob } from 'src/api/queries/jobs/useJob';
import { ProgressViewerScopes } from 'src/data/scopes';
import { getErrorMessage } from 'src/utils';
import { RoleBasedRoute } from 'src/app/authorization/RoleBasedRoute';
import { NotFound } from 'src/app/navigation/NotFound';
import { useJobs } from 'src/hooks/useJobs';
import { DesignModels } from 'src/routes/Jobs/DesignModels';
import { ViewDesignModel } from 'src/routes/Jobs/DesignModels/View';
import { Templates } from 'src/routes/Jobs/Templates';
import { ActivityFeed } from './ActivityFeed';
import { Changes } from './Changes';
import { CreateChange } from './Changes/CreateChange';
import { ViewChange } from './Changes/ViewChange';
import { Contacts } from './Contacts';
import { Dailys } from './Dailys';
import { ViewDaily } from './Dailys/ViewDaily';
import { Documents } from './Documents';
import { Drawings } from './Drawings';
import { SubmitDrawing } from './Drawings/Submit';
import { ViewDrawing } from './Drawings/View';
import { Emails } from './Emails';
import { Hyperlinks } from './Hyperlinks';
import { MeetingMinutes } from './MeetingMinutes';
import { CreateMeetingMinutes } from './MeetingMinutes/Create';
import { ExportMeeting } from './MeetingMinutes/ExportMeeting';
import { ViewMeetingMinutes } from './MeetingMinutes/View';
import { Milestones } from './Milestones';
import { Questions } from './Questions';
import { Rates } from './Rates';
import { RFIs } from './RFIs';
import { SubmitRFI } from './RFIs/SubmitRFI';
import { ViewRFI } from './RFIs/ViewRFI';
import { Submittals } from './Submittals';
import { SubmitSubmittal } from './Submittals/Submit';
import { ViewSubmittal } from './Submittals/View';
import { Summary } from './Summary';
import { Team } from './Team';
import { Tickets } from './Tickets';
import { CreateTicket } from './Tickets/CreateTicket';
import { EditTicket } from './Tickets/EditTicket';
import { Timesheets } from './Timesheets';
import { ViewTimesheet } from './Timesheets/View/ViewTimesheet';
import { TrackerPage } from './Tracker';
import { Transmittals } from './Transmittals';
import { SubmitTransmittal } from './Transmittals/Submit';
import { ViewTransmittal } from './Transmittals/View';
import { WBS } from './WBS';
import { Workflows } from './Workflows';
import { EditWorkflow } from './Workflows/EditWorkflow';

// A static import here pulls the three.js viewer back into the main bundle.
const Progress = React.lazy(() =>
  import('./Progress').then((m) => ({ default: m.Progress })),
);

interface ISettingsRoutes {
  requiredScopes: string[];
}

export const JobRouter: React.FC = () => {
  const params = useParams();
  const routeJobId = Number(params.id);
  const { selectedJob, setSelectedJob } = useJobs();
  const { data: job, isLoading, error } = useJob(routeJobId);

  React.useEffect(() => {
    if (job && selectedJob?.id !== job.id) setSelectedJob(job);
  }, [job, selectedJob?.id, setSelectedJob]);

  if (error) {
    return <p>{getErrorMessage(error)}</p>;
  }

  if (isLoading || !job || selectedJob?.id !== job.id) {
    return (
      <Box style={{ width: '100%', textAlign: 'center' }}>
        <Loader style={{ margin: 'auto', marginTop: '20vh' }} />
        <h3>Loading Job Insites...</h3>
      </Box>
    );
  }

  return (
    <Routes>
      <Route
        path="/summary"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_summary']}
            render={() => <Summary />}
          />
        }
      />
      <Route
        path="/team"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_settings_all', 'jobs_settings_team']}
            render={() => <Team />}
          />
        }
      />
      <Route
        path="/hyperlinks"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_settings_all', 'jobs_settings_hyperlinks']}
            render={() => <Hyperlinks />}
          />
        }
      />
      <Route
        path="/workflows"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_settings_all', 'jobs_settings_workflows']}
            render={() => <Workflows />}
          />
        }
      />
      <Route
        path="/templates"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_settings_all', 'jobs_settings_templates']}
            render={() => <Templates />}
          />
        }
      />
      <Route
        path="/design-models"
        element={
          <RoleBasedRoute
            requiredScopes={[
              'jobs_settings_all',
              'jobs_settings_design_models',
            ]}
            render={() => <DesignModels />}
          />
        }
      />
      <Route
        path="/design-models/:designModelId"
        element={
          <RoleBasedRoute
            requiredScopes={[
              'jobs_settings_all',
              'jobs_settings_design_models',
            ]}
            render={() => <ViewDesignModel />}
          />
        }
      />
      <Route
        path="/progress"
        element={
          <RoleBasedRoute
            requiredScopes={ProgressViewerScopes}
            render={() => (
              <Suspense
                fallback={
                  <Center mt="20vh">
                    <Loader />
                  </Center>
                }
              >
                <Progress />
              </Suspense>
            )}
          />
        }
      />

      <Route
        path="/emails"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_emails']}
            render={() => <Emails />}
          />
        }
      />
      <Route
        path="/workflows/:workflowId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_settings_all', 'jobs_settings_workflows']}
            render={() => <EditWorkflow />}
          />
        }
      />
      <Route
        path="/documents"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_documents']}
            render={() => <Documents />}
          />
        }
      />
      <Route
        path="/drawings"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_drawings']}
            render={() => <Drawings />}
          />
        }
      />
      <Route
        path="/wbs"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_settings_all', 'jobs_settings_wbs']}
            render={() => <WBS />}
          />
        }
      />

      <Route
        path="/drawings/:drawingId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_drawings']}
            render={() => <ViewDrawing />}
          />
        }
      />

      <Route
        path="/submittals"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_submittals']}
            render={() => <Submittals />}
          />
        }
      />
      <Route
        path="/submittals/:submittalId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_submittals']}
            render={() => <ViewSubmittal />}
          />
        }
      />

      <Route
        path="/submittals/submit/submittal"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_submittals']}
            render={() => <SubmitSubmittal />}
          />
        }
      />
      <Route
        path="/drawings/submit/drawing"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_drawings']}
            render={() => <SubmitDrawing />}
          />
        }
      />
      <Route
        path="/transmittals"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_transmittals']}
            render={() => <Transmittals />}
          />
        }
      />
      <Route
        path="/transmittals/submit/transmittal"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_transmittals']}
            render={() => <SubmitTransmittal />}
          />
        }
      />
      <Route
        path="/transmittals/:transmittalId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_transmittals']}
            render={() => <ViewTransmittal />}
          />
        }
      />
      <Route
        path="/rfis"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_rfis']}
            render={() => <RFIs />}
          />
        }
      />
      <Route
        path="/rfis/:rfiId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_rfis']}
            render={() => <ViewRFI />}
          />
        }
      />
      <Route
        path="/rfis/submit/rfi"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_rfis']}
            render={() => <SubmitRFI />}
          />
        }
      />
      <Route
        path="/timesheets"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_timesheets']}
            render={() => <Timesheets />}
          />
        }
      />
      <Route
        path="/timesheets/user/:userId/week/:year/:weekId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_timesheets']}
            render={() => <ViewTimesheet />}
          />
        }
      />
      <Route
        path="/activity"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_activity']}
            render={() => <ActivityFeed />}
          />
        }
      />
      <Route
        path="/milestones"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_milestones']}
            render={() => <Milestones />}
          />
        }
      />
      <Route
        path="/contacts"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_contacts']}
            render={() => <Contacts />}
          />
        }
      />
      <Route
        path="/dailys/view/:dailyId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_dailys']}
            render={() => <ViewDaily />}
          />
        }
      />
      <Route
        path="/dailys"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_dailys']}
            render={() => <Dailys />}
          />
        }
      />
      <Route
        path="/tickets"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_work_tickets']}
            render={() => <Tickets />}
          />
        }
      />
      <Route
        path="/tickets/create"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_work_tickets']}
            render={() => <CreateTicket />}
          />
        }
      />
      <Route
        path="/tickets/view/:ticketId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_work_tickets']}
            render={() => <EditTicket />}
          />
        }
      />

      <Route
        path="/meetings"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_meeting_minutes']}
            render={() => <MeetingMinutes />}
          />
        }
      />
      <Route
        path="/tracker"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_tracker']}
            render={() => <TrackerPage />}
          />
        }
      />
      <Route
        path="/meetings/export/:meetingId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_meeting_minutes']}
            render={() => <ExportMeeting />}
          />
        }
      />
      <Route
        path="/meetings/create"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_meeting_minutes']}
            render={() => <CreateMeetingMinutes />}
          />
        }
      />
      <Route
        path="/meetings/edit/:meetingId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_meeting_minutes']}
            render={() => <ViewMeetingMinutes />}
          />
        }
      />
      <Route
        path="/questions"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_settings_all', 'jobs_settings_questions']}
            render={() => <Questions />}
          />
        }
      />
      <Route
        path="/rates"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_settings_all', 'jobs_settings_rates']}
            render={() => <Rates />}
          />
        }
      />
      <Route
        path="/changes"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_changes']}
            render={() => <Changes />}
          />
        }
      />
      <Route
        path="/changes/submit/change"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_changes']}
            render={() => <CreateChange />}
          />
        }
      />
      <Route
        path="/changes/view/:changeId"
        element={
          <RoleBasedRoute
            requiredScopes={['jobs_all', 'jobs_changes']}
            render={() => <ViewChange />}
          />
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
